<script setup lang="ts">
  import { ref } from 'vue';
  import { IconImage, DatePicker, FilterModal } from '@/shared/ui/';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import ArrowRight from '@/shared/assets/icons/ArrowRight.svg';
  import { useTestContentFilter } from '../model/useTestContentFilter';
  import type { FilterOptions } from '@/entities/Document';
  import { formatDateToPtBR } from '@/shared/lib';

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: FilterOptions): void;
    (e: 'clear-filters'): void;
  }>();

  const { formData, usersList, clearFilters, applyFilters } = useTestContentFilter(
    (event, filters) => {
      emit(event, filters);
    }
  );

  const handleClearFilters = () => {
    clearFilters();
    emit('clear-filters');
  };

  const isRequestDateOpen = ref(false);
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @clear-filters="handleClearFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <div class="px-6 space-y-4">
        <div class="w-full py-3 px-4 pb-6 gap-4 border-b border-gray-02">
          <h4 class="font-medium text-gray-07 mb-2">
            Elaborado por
          </h4>
          <div class="relative">
            <select
              v-model="formData.elaboratedBy"
              class="w-full px-4 py-3 rounded-lg border border-gray-02 text-gray-03 appearance-none"
            >
              <option value="">
                Selecione um colaborador
              </option>
              <option
                v-for="user in usersList"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name || 'Nome não disponível' }}
              </option>
            </select>
            <div class="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <IconImage
                :src="ArrowRight"
                alt="Seta"
                class="h-8 w-8 rotate-90"
              />
            </div>
          </div>
        </div>

        <div class="w-full py-3 px-4 pb-6 gap-4 border-b border-gray-02">
          <h4 class="font-medium text-gray-07 mb-2">
            Aprovado por
          </h4>
          <div class="relative">
            <select
              v-model="formData.approvedBy"
              class="w-full px-4 py-3 rounded-lg border border-gray-02 text-gray-03 appearance-none"
            >
              <option value="">
                Selecione um colaborador
              </option>
              <option
                v-for="user in usersList"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name || 'Nome não disponível' }}
              </option>
            </select>
            <div class="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <IconImage
                :src="ArrowRight"
                alt="Seta"
                class="h-8 w-8 rotate-90"
              />
            </div>
          </div>
        </div>

        <div class="w-full py-3 px-4 pb-6 gap-4">
          <h4 class="font-medium text-gray-07 pb-4">
            Data da Solicitação
          </h4>
          <button
            class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium gap-2"
            @click="isRequestDateOpen = true"
          >
            <IconImage
              :src="CalendarIcon"
              alt="Calendário"
              class="h-5 w-5"
            />
            <span class="grow text-center">{{
              formatDateToPtBR(formData.requestDate) || 'Selecione uma data'
            }}</span>
          </button>
          <DatePicker
            v-model:selected-date="formData.requestDate"
            v-model:is-open="isRequestDateOpen"
            class="my-auto"
            @apply="isRequestDateOpen = false"
            @cancel="isRequestDateOpen = false"
          />
        </div>
      </div>
    </template>
  </FilterModal>
</template>
